import { Button } from "@/lib/components/Button/Button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/lib/components/DropdownMenu";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import Toast from "@/lib/components/Toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { queryClient } from "@/lib/queries/query-client";
import {
  useDeleteVideoRenderFromVimeoMutation,
  videoRenderQueryKeys,
} from "@/lib/queries/video-render-queries";
import { cn, downloadFile } from "@/lib/utils";
import { VideoDto } from "@repo/api/editor";
import { VideoRenderDto, VideoRenderExportType } from "@repo/api/video-render";
import { formatVimeoLink } from "@repo/api/utils/video-render";
import {
  IconAlertCircle,
  IconCopy,
  IconDots,
  IconDownload,
  IconLink,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import fileIcon from "./icons/file-icon.png";
import vimeoLogo from "./icons/vimeo-logo.png";
import { estimateRenderTime } from "./export-video-utils";

export type ExportLinkStatus = "rendering" | "failed" | "done" | "deleted";

type Props = {
  mode: VideoRenderExportType;
  status: ExportLinkStatus;
  videoRender: VideoRenderDto;
  video: VideoDto;
  videoDurationMs: number;
  onShare?: () => void;
};

function formatLink(videoRender: VideoRenderDto) {
  const uri =
    videoRender.vimeoInfo?.status === "done" ? videoRender.vimeoInfo?.uri : "";

  return formatVimeoLink(uri);
}

function VimeoInfoTooltip({
  children,
  videoRender,
  status,
}: {
  children: React.ReactNode;
  videoRender: VideoRenderDto;
  status: ExportLinkStatus;
}) {
  const [isDelayed, setIsDelayed] = useState(status !== "done");

  useEffect(() => {
    if (isDelayed && status === "done") {
      const timeout = setTimeout(() => {
        setIsDelayed(false);
      }, 700);

      return () => clearTimeout(timeout);
    }
  }, [status, isDelayed]);

  const isNewRender =
    new Date(videoRender.createdAt) > new Date(Date.now() - 1000 * 60 * 10); // eslint-disable-line react-hooks/purity

  if (isDelayed) {
    return children;
  }

  return (
    <Tooltip defaultOpen={isNewRender}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-center text-xs md:text-sm">
          Vimeo might say{" "}
          <span className="font-medium italic">
            "Sorry, we're having a little trouble"
          </span>
          <br />
          for the first few minutes before the video is visible.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

function StatusIcon({
  mode,
  status,
}: {
  mode: VideoRenderExportType;
  status: ExportLinkStatus;
}) {
  if (status === "rendering") {
    return <LoadingSpinner size={20} />;
  }

  if (status === "failed") {
    return <IconAlertCircle className="h-5 w-5 text-error" />;
  }

  if (status === "deleted") {
    return <IconTrash className="h-5 w-5 text-error" />;
  }

  return mode === "VIMEO" ? (
    <img src={vimeoLogo} className="h-5 w-5" />
  ) : (
    <img src={fileIcon} className="h-5 w-5" />
  );
}

export function ExportLinkButton({
  mode,
  status,
  videoRender,
  video,
  videoDurationMs,
  onShare,
}: Props) {
  const { teamExternalId } = useParams();

  const { mutate: deleteVideoRenderFromVimeo, isPending: isDeletingFromVimeo } =
    useDeleteVideoRenderFromVimeoMutation({
      teamExternalId: teamExternalId!,
      videoRenderExternalId: videoRender.externalId,
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: videoRenderQueryKeys.getVideoRendersForVideo(
            teamExternalId!,
            video.externalId,
          ),
        });
        Toast.info("Video deleted from Vimeo successfully");
      },
      onError: (error) => {
        console.error(error);
        Toast.error("Failed to delete video from Vimeo");
      },
    });

  const estimatedMinutes = estimateRenderTime(
    videoDurationMs,
    videoRender.upscaleQuality,
  );

  const secondaryLabel =
    status === "rendering"
      ? `This takes about ${estimatedMinutes} minute${estimatedMinutes === 1 ? "" : "s"}`
      : mode === "VIMEO"
        ? "Vimeo link"
        : "Video file";

  const card = (
    <div className="flex w-full justify-center px-8 pt-8">
      <div className="relative flex w-full max-w-115 flex-row items-center gap-4 overflow-hidden rounded-lg border border-border px-4 py-2.5 md:w-115">
        <div className="flex h-10 w-10 items-center justify-center">
          <StatusIcon mode={mode} status={status} />
        </div>
        <div
          className={cn(
            "flex flex-1 flex-col overflow-hidden",
            status === "rendering" && "flex-col-reverse",
          )}
        >
          <p className="line-clamp-1 text-xs text-ellipsis text-text-secondary">
            {secondaryLabel}
          </p>
          <p className="line-clamp-1 truncate text-sm font-medium">
            {status === "rendering" && "Rendering video..."}
            {status === "failed" &&
              (mode === "VIMEO"
                ? "Failed to upload video to Vimeo"
                : "Failed to render video")}
            {status === "deleted" && "Video deleted from Vimeo"}
            {status === "done" && mode === "VIMEO" && !isDeletingFromVimeo && (
              <a
                href={`https://${formatLink(videoRender)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatLink(videoRender)}
              </a>
            )}
            {status === "done" &&
              mode === "DOWNLOAD" &&
              (videoRender.externalFileName
                ? `${videoRender.externalFileName}.mp4`
                : "Exported video")}
          </p>
        </div>

        {status === "done" && mode === "DOWNLOAD" && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="iconSm"
              className="[&_svg]:size-5"
              onClick={async () => {
                if (!videoRender.downloadUrl) {
                  return;
                }

                try {
                  downloadFile(videoRender.downloadUrl);
                } catch (error) {
                  console.error("Failed to download video file:", error);
                  Toast.error("Failed to download video file");
                }
              }}
            >
              <IconDownload />
            </Button>
            {onShare && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="iconXs">
                    <IconDots size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top">
                  <DropdownMenuItem onClick={onShare}>
                    <IconLink />
                    <span>Share link</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        {status === "done" && mode === "VIMEO" && (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="iconSm"
              className="[&_svg]:size-5"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(
                    `https://${formatLink(videoRender)}`,
                  );
                  Toast.info("Vimeo link copied to clipboard!");
                } catch (error) {
                  console.error("Failed to copy to clipboard:", error);
                  Toast.error("Failed to copy Vimeo link");
                }
              }}
            >
              <IconCopy />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="iconXs">
                  <IconDots size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top">
                <DropdownMenuItem
                  onClick={() => deleteVideoRenderFromVimeo()}
                  disabled={isDeletingFromVimeo}
                >
                  <IconTrash />
                  <span>Delete video from Vimeo</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {mode === "VIMEO" && isDeletingFromVimeo && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white">
            <LoadingSpinner size={16} />
            <span className="text-sm font-medium">
              Deleting video from Vimeo...
            </span>
          </div>
        )}
      </div>
    </div>
  );

  if (mode === "VIMEO") {
    return (
      <VimeoInfoTooltip videoRender={videoRender} status={status}>
        {card}
      </VimeoInfoTooltip>
    );
  }

  return card;
}
